import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import { useTheme } from 'styled-components';
import useSWR from 'swr';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { capitalizeFirstLetter } from '../../../../utils';
import isEmpty from '../../../../utils/isEmpty';
import Button from '../../../common/base/Button';
import Icon from '../../../common/base/Icon';
import { ChartDataSet, ChartTimeOptions } from '../../../common/Chart/Chart';
import { generateRandomChartColor } from '../../../common/Chart/LegendColor';
import DropdownMenu from '../../../common/DropdownMenu';
import { Div } from '../../../common/helpers/StyledUtils';
import { default_data_formatter, MaxMetricTotalStrategy, metric_definitions, MetricName } from '../../../common/metrics/metric-definitions';
import MetricChartBase from '../../../common/metrics/MetricChart';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { useRefreshKey } from './RefreshKeyContext';
import { getDateRangeQueryParam } from './MetricDatePicker';
import { getMaxMetricTotal } from './metricTotals';
import { ChartData, MetricConfigs, OTHER_SERIES_KEY, useMetric } from './useMetric';
const resource_dimensions = {
  destination_id: 'destinations',
  source_id: 'sources',
  webhook_id: 'webhooks'
} as const;
const dimension_labels = {
  delivery_group: 'Delivery group'
} as const;
export function useMetricChartData(metric, configs, options) {
  const $ = _c(87);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const theme = useTheme();
  const {
    refresh_key
  } = useRefreshKey();
  const force_legend = options?.force_legend ?? false;
  const hide_total = options?.hide_total ?? false;
  const chart_type_option = options?.chart_type;
  const title_option = options?.title;
  const on_expand = options?.onExpand;
  const expanded = options?.expanded;
  const dimension = configs.options?.dimensions?.[0];
  const resource = dimension && dimension in resource_dimensions ? resource_dimensions[dimension as keyof typeof resource_dimensions] : undefined;
  const legend_placeholder = dimension && dimension in dimension_labels ? dimension_labels[dimension as keyof typeof dimension_labels] : undefined;
  const {
    title: metric_title,
    chart_type: chart_type_override,
    description,
    description_cta,
    type,
    tooltip_label,
    formatDataForDisplay,
    max_metric_total_strategy: metric_max_metric_total_strategy
  } = metric_definitions[metric];
  const max_metric_total_strategy = options?.max_metric_total_strategy ?? metric_max_metric_total_strategy ?? "latest_max";
  const chart_type = chart_type_option || chart_type_override || (type === "count" ? "bar" : "line");
  const metric_chart_type = chart_type === "bar" ? "chart:bar" : "chart:line";
  const {
    data,
    error,
    rate_as,
    is_loading
  } = useMetric(metric_chart_type, metric, refresh_key, configs);
  const is_array = Array.isArray(data?.data);
  let t0;
  let t1;
  let unique_ids;
  if ($[0] !== data || $[1] !== is_array || $[2] !== resource) {
    unique_ids = !is_array && data ? Object.entries(data).map(_temp).filter(_temp2) : [];
    t0 = useSWR;
    t1 = resource && unique_ids.length > 0 ? APIMethodKeys[resource].list({
      id: unique_ids,
      archived: true
    }) : null;
    $[0] = data;
    $[1] = is_array;
    $[2] = resource;
    $[3] = t0;
    $[4] = t1;
    $[5] = unique_ids;
  } else {
    t0 = $[3];
    t1 = $[4];
    unique_ids = $[5];
  }
  let t2;
  if ($[6] !== HookdeckAPI || $[7] !== resource || $[8] !== unique_ids) {
    t2 = () => {
      if (!resource) {
        return null;
      }
      return HookdeckAPI[resource].list(unique_ids.length > 0 ? {
        id: unique_ids,
        archived: true
      } : {
        limit: 1
      }) as any;
    };
    $[6] = HookdeckAPI;
    $[7] = resource;
    $[8] = unique_ids;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const {
    data: resources
  } = t0(t1, t2);
  let action;
  let formated_data;
  let t3;
  if ($[10] !== configs.date_range || $[11] !== configs.options?.filters || $[12] !== data || $[13] !== expanded || $[14] !== force_legend || $[15] !== formatDataForDisplay || $[16] !== hide_total || $[17] !== is_array || $[18] !== max_metric_total_strategy || $[19] !== metric || $[20] !== on_expand || $[21] !== rate_as || $[22] !== resource || $[23] !== resources || $[24] !== theme || $[25] !== tooltip_label || $[26] !== type || $[27] !== unique_ids) {
    const resources_by_id = resources?.models ? resources.models.reduce(_temp3, {}) : {};
    formated_data = [];
    if (is_array) {
      data && formated_data.push({
        ...data,
        key: metric,
        label: tooltip_label,
        getDataLabel: formatDataForDisplay || (v => default_data_formatter[type](v, rate_as || undefined)),
        theme_color: "primary",
        metric_type: type
      });
    } else {
      const should_use_resource_labels = !!resource;
      formated_data = data && !isEmpty(data) && (!should_use_resource_labels || !isEmpty(resources_by_id) || (data as unknown as Record<string, ChartData>)[OTHER_SERIES_KEY]) ? Object.entries(data).map(t4 => {
        const [key_1, data_entry] = t4;
        if (key_1 === OTHER_SERIES_KEY) {
          return {
            ...data_entry,
            key: key_1,
            label: "Other",
            getDataLabel: formatDataForDisplay || (v_0 => default_data_formatter[type](v_0, rate_as || undefined)),
            theme_color: undefined,
            hex_color: theme.colors.on.neutral.disabled,
            metric_type: type
          };
        }
        const resource_obj = resources_by_id?.[key_1];
        return {
          ...data_entry,
          key: key_1,
          label: should_use_resource_labels ? resource_obj ? resource === "webhooks" ? resource_obj?.full_name : resource_obj?.name : "Deleted" : key_1 || "Unknown",
          getDataLabel: formatDataForDisplay || (v_1 => default_data_formatter[type](v_1, rate_as || undefined)),
          theme_color: unique_ids.length > 1 || force_legend ? undefined : "primary",
          hex_color: unique_ids.length > 1 || force_legend ? !should_use_resource_labels || resource_obj ? generateRandomChartColor(key_1) : theme.colors.on.neutral.disabled : undefined,
          metric_type: type
        };
      }) : [];
    }
    action = undefined;
    if (metric_definitions[metric].buildExploreLink && on_expand) {
      if (expanded) {
        let t4;
        if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = {
            align: "center",
            gap: 3
          };
          $[31] = t4;
        } else {
          t4 = $[31];
        }
        let t5;
        if ($[32] !== configs.date_range || $[33] !== configs.options?.filters || $[34] !== metric) {
          t5 = metric_definitions[metric].buildExploreLink?.({
            date: getDateRangeQueryParam(configs.date_range),
            ...configs.options?.filters
          });
          $[32] = configs.date_range;
          $[33] = configs.options?.filters;
          $[34] = metric;
          $[35] = t5;
        } else {
          t5 = $[35];
        }
        let t6;
        if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <Icon icon="link" pointer={true} right={2} />;
          $[36] = t6;
        } else {
          t6 = $[36];
        }
        let t7;
        if ($[37] !== t5) {
          t7 = <Button ghost={true} small={true} muted={true} to={t5}>Explore{t6}</Button>;
          $[37] = t5;
          $[38] = t7;
        } else {
          t7 = $[38];
        }
        let t8;
        if ($[39] !== on_expand) {
          t8 = <Button outline={true} small={true} icon="close" ghost={true} onClick={on_expand} />;
          $[39] = on_expand;
          $[40] = t8;
        } else {
          t8 = $[40];
        }
        let t9;
        if ($[41] !== t7 || $[42] !== t8) {
          t9 = <Div flex={t4}>{t7}{t8}</Div>;
          $[41] = t7;
          $[42] = t8;
          $[43] = t9;
        } else {
          t9 = $[43];
        }
        action = t9;
      } else {
        let t4;
        if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = {
            px: 184
          };
          $[44] = t4;
        } else {
          t4 = $[44];
        }
        let t5;
        if ($[45] !== configs.date_range || $[46] !== configs.options?.filters || $[47] !== metric) {
          t5 = metric_definitions[metric].buildExploreLink?.({
            date: getDateRangeQueryParam(configs.date_range),
            ...configs.options?.filters
          });
          $[45] = configs.date_range;
          $[46] = configs.options?.filters;
          $[47] = metric;
          $[48] = t5;
        } else {
          t5 = $[48];
        }
        let t6;
        if ($[49] !== t5) {
          t6 = {
            label: "Explore",
            icon: "link",
            to: t5
          };
          $[49] = t5;
          $[50] = t6;
        } else {
          t6 = $[50];
        }
        let t7;
        if ($[51] !== on_expand) {
          t7 = {
            label: "Expand",
            icon: "open",
            onClick: on_expand
          };
          $[51] = on_expand;
          $[52] = t7;
        } else {
          t7 = $[52];
        }
        let t8;
        if ($[53] !== t6 || $[54] !== t7) {
          t8 = <DropdownMenu ghost={true} small={true} icon="horizontal_more" w={t4} placement="bottom-end" options={[t6, t7]} />;
          $[53] = t6;
          $[54] = t7;
          $[55] = t8;
        } else {
          t8 = $[55];
        }
        action = t8;
      }
    } else {
      if (on_expand) {
        const t4 = expanded ? "close" : "open";
        const t5 = !expanded;
        let t6;
        if ($[56] !== expanded || $[57] !== on_expand || $[58] !== t4 || $[59] !== t5) {
          t6 = <Button small={true} icon={t4} outline={expanded} ghost={t5} onClick={on_expand} />;
          $[56] = expanded;
          $[57] = on_expand;
          $[58] = t4;
          $[59] = t5;
          $[60] = t6;
        } else {
          t6 = $[60];
        }
        action = t6;
      } else {
        if (metric_definitions[metric].buildExploreLink) {
          let t4;
          if ($[61] !== configs.date_range || $[62] !== configs.options?.filters || $[63] !== metric) {
            t4 = metric_definitions[metric].buildExploreLink?.({
              date: getDateRangeQueryParam(configs.date_range),
              ...configs.options?.filters
            });
            $[61] = configs.date_range;
            $[62] = configs.options?.filters;
            $[63] = metric;
            $[64] = t4;
          } else {
            t4 = $[64];
          }
          let t5;
          if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
            t5 = <Icon icon="link" pointer={true} right={2} />;
            $[65] = t5;
          } else {
            t5 = $[65];
          }
          let t6;
          if ($[66] !== t4) {
            t6 = <Button ghost={true} small={true} muted={true} to={t4}>Explore{t5}</Button>;
            $[66] = t4;
            $[67] = t6;
          } else {
            t6 = $[67];
          }
          action = t6;
        }
      }
    }
    let total = undefined;
    if (formated_data && !hide_total) {
      if (type === "rate") {
        total = Object.values(formated_data).reduce(_temp4, 0);
      } else {
        if (type === "count") {
          total = Object.values(formated_data).reduce(_temp5, 0);
        } else {
          if (type === "max") {
            total = getMaxMetricTotal(Object.values(formated_data), max_metric_total_strategy);
          } else {
            if (type === "average") {
              total = Object.values(formated_data).reduce(_temp6, 0) / Object.values(formated_data).length;
            } else {
              if (type === "percent") {
                const samples_with_data = Object.values(formated_data).reduce(_temp7, 0);
                total = samples_with_data > 0 ? Object.values(formated_data).reduce(_temp8, 0) / samples_with_data : Object.values(formated_data).reduce(_temp9, 0) / Object.values(formated_data).length;
              }
            }
          }
        }
      }
    }
    t3 = total === undefined || typeof total === "number" && Number.isNaN(total) ? undefined : metric_definitions[metric].formatDataForDisplay?.(total) || default_data_formatter[type](total, rate_as || undefined);
    $[10] = configs.date_range;
    $[11] = configs.options?.filters;
    $[12] = data;
    $[13] = expanded;
    $[14] = force_legend;
    $[15] = formatDataForDisplay;
    $[16] = hide_total;
    $[17] = is_array;
    $[18] = max_metric_total_strategy;
    $[19] = metric;
    $[20] = on_expand;
    $[21] = rate_as;
    $[22] = resource;
    $[23] = resources;
    $[24] = theme;
    $[25] = tooltip_label;
    $[26] = type;
    $[27] = unique_ids;
    $[28] = action;
    $[29] = formated_data;
    $[30] = t3;
  } else {
    action = $[28];
    formated_data = $[29];
    t3 = $[30];
  }
  const formatted_total = t3;
  let t4;
  if ($[68] !== formated_data) {
    t4 = !formated_data ? [] : formated_data;
    $[68] = formated_data;
    $[69] = t4;
  } else {
    t4 = $[69];
  }
  const t5 = title_option || metric_title;
  let t6;
  if ($[70] !== configs.options?.dimensions || $[71] !== legend_placeholder || $[72] !== resource) {
    t6 = configs.options?.dimensions && (resource ? capitalizeFirstLetter(resource) : legend_placeholder);
    $[70] = configs.options?.dimensions;
    $[71] = legend_placeholder;
    $[72] = resource;
    $[73] = t6;
  } else {
    t6 = $[73];
  }
  const t7 = type === "rate" ? `per ${rate_as}` : type === "percent" ? "%" : undefined;
  const t8 = !!error;
  let t9;
  if ($[74] !== action || $[75] !== chart_type || $[76] !== description || $[77] !== description_cta || $[78] !== force_legend || $[79] !== formatted_total || $[80] !== is_loading || $[81] !== t4 || $[82] !== t5 || $[83] !== t6 || $[84] !== t7 || $[85] !== t8) {
    t9 = {
      chart_type,
      datasets: t4,
      title: t5,
      title_tooltip: description,
      title_tooltip_cta: description_cta,
      legend_placeholder: t6,
      force_legend,
      total: formatted_total,
      total_hint: t7,
      loading: is_loading,
      error: t8,
      action
    };
    $[74] = action;
    $[75] = chart_type;
    $[76] = description;
    $[77] = description_cta;
    $[78] = force_legend;
    $[79] = formatted_total;
    $[80] = is_loading;
    $[81] = t4;
    $[82] = t5;
    $[83] = t6;
    $[84] = t7;
    $[85] = t8;
    $[86] = t9;
  } else {
    t9 = $[86];
  }
  return t9;
}
function _temp9(acc_4, curr_4) {
  return acc_4 + curr_4.average;
}
function _temp8(acc_3, curr_3) {
  return acc_3 + curr_3.average * (curr_3.samples_with_data || 0);
}
function _temp7(acc_2, curr_2) {
  return acc_2 + (curr_2.samples_with_data || 0);
}
function _temp6(acc_1, curr_1) {
  return acc_1 + curr_1.average;
}
function _temp5(acc_0, curr_0) {
  return acc_0 + curr_0.total;
}
function _temp4(acc, curr) {
  return acc + curr.average;
}
function _temp3(object, resource_0) {
  return {
    ...object,
    [resource_0.id]: resource_0
  };
}
function _temp2(key_0) {
  return key_0 !== OTHER_SERIES_KEY;
}
function _temp(t0) {
  const [key] = t0;
  return key;
}
const MetricChart: React.FC<{
  metric: MetricName;
  configs: MetricConfigs;
  force_legend?: boolean;
  hide_total?: boolean;
  chart_type?: 'bar' | 'line';
  title?: string;
  height?: number;
  time_options?: ChartTimeOptions;
  onExpand?: () => void;
  expanded?: boolean;
  max_metric_total_strategy?: MaxMetricTotalStrategy;
}> = t0 => {
  const $ = _c(12);
  const {
    metric,
    configs,
    force_legend,
    hide_total,
    chart_type,
    title,
    height,
    time_options,
    onExpand,
    expanded,
    max_metric_total_strategy
  } = t0;
  let t1;
  if ($[0] !== chart_type || $[1] !== expanded || $[2] !== force_legend || $[3] !== hide_total || $[4] !== max_metric_total_strategy || $[5] !== onExpand || $[6] !== title) {
    t1 = {
      force_legend,
      hide_total,
      chart_type,
      title,
      onExpand,
      expanded,
      max_metric_total_strategy
    };
    $[0] = chart_type;
    $[1] = expanded;
    $[2] = force_legend;
    $[3] = hide_total;
    $[4] = max_metric_total_strategy;
    $[5] = onExpand;
    $[6] = title;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const hook_data = useMetricChartData(metric, configs, t1);
  let t2;
  if ($[8] !== height || $[9] !== hook_data || $[10] !== time_options) {
    t2 = <MetricChartBase {...hook_data} height={height} time_options={time_options} />;
    $[8] = height;
    $[9] = hook_data;
    $[10] = time_options;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};
export default MetricChart;